
import React, { useEffect, useMemo, useState } from 'react';

export default function MaintenanceGroundTruthModal({
  open,
  robotLabel,
  robotSerial,
  headNames = [],
  selectedHead = null,
  predictedByHead = {},
  onCancel,
  onSkip,
  onSave,
  saving = false,
}) {
  const normalizedHeadNames = useMemo(() => {
    if (Array.isArray(headNames) && headNames.length) {
      return headNames.map((head) => String(head || '').trim()).filter(Boolean);
    }
    return [];
  }, [headNames]);

  const [missingByHead, setMissingByHead] = useState({});
  const [replacedByHead, setReplacedByHead] = useState({});
  const [checkedHeads, setCheckedHeads] = useState({});
  const [inspectionOutcome, setInspectionOutcome] = useState('');
  const [missingBladeCount, setMissingBladeCount] = useState('');
  const [damagedBladeCount, setDamagedBladeCount] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    const initial = normalizedHeadNames.reduce((acc, head) => {
      acc[head] = '';
      return acc;
    }, {});
    setMissingByHead(initial);
    setReplacedByHead(initial);
    setCheckedHeads(selectedHead && initial[selectedHead] !== undefined ? { [selectedHead]: true } : {});
    setInspectionOutcome('');
    setMissingBladeCount('');
    setDamagedBladeCount('');
    setNotes('');
  }, [open, normalizedHeadNames, selectedHead]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="rbh-modal-overlay"
    >
      <div className="rbh-modal-surface maintenance-ground-truth-modal">
        <h3 style={{ marginTop: 0 }}>Fysieke maaikopcontrole</h3>
        <div className="kleine-tekst" style={{ marginBottom: 'var(--space-8)' }}>
          Robot: <strong>{robotLabel || robotSerial || '-'}</strong>
        </div>
        <div className="kleine-tekst" style={{ marginBottom: 'var(--space-12)' }}>
          {selectedHead ? `Controleer ${selectedHead}. ` : ''}Leg alleen vast wat fysiek is vastgesteld; de voorspelling is geen inspectieresultaat.
        </div>
        <div className="rbh-modal-context-card" aria-label="Voorspelling">
          <strong>Wat RBH voorspelde</strong>
          {normalizedHeadNames.map((head) => (
            <span key={head}>{head}: {Number(predictedByHead?.[head] ?? 0)} ontbrekende messen</span>
          ))}
          <strong>Wat ik fysiek vond</strong>
        </div>

        <label className="kleine-tekst" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 'var(--space-12)' }}>
          <span>Inspectie-uitkomst</span>
          <select className="input-bordered" value={inspectionOutcome} onChange={(event) => setInspectionOutcome(event.target.value)}>
            <option value="">Kies een uitkomst</option>
            <option value="all_clear">Alles in orde</option>
            <option value="missing_blades">Mes ontbreekt</option>
            <option value="damaged_blades">Mes beschadigd</option>
            <option value="other_head_issue">Andere maaikopafwijking</option>
            <option value="not_checkable">Niet controleerbaar</option>
          </select>
        </label>

        <div className="maintenance-ground-truth-modal__summary-grid">
          <label className="kleine-tekst" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>Ontbrekende messen</span>
            <input type="number" min="0" step="1" placeholder="Onbekend" className="input-bordered" value={missingBladeCount} onChange={(event) => setMissingBladeCount(event.target.value)} />
          </label>
          <label className="kleine-tekst" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>Beschadigde messen</span>
            <input type="number" min="0" step="1" placeholder="Onbekend" className="input-bordered" value={damagedBladeCount} onChange={(event) => setDamagedBladeCount(event.target.value)} />
          </label>
        </div>

        <div className="kleine-tekst" style={{ marginBottom: 'var(--space-8)' }}>Fysieke controle per maaikop</div>

        {normalizedHeadNames.length > 0 && (
          <button
            type="button"
            className="mwa-button secondary"
            onClick={() => {
              setInspectionOutcome('all_clear');
              setMissingBladeCount('0');
              setCheckedHeads(Object.fromEntries(normalizedHeadNames.map((head) => [head, true])));
              setMissingByHead(Object.fromEntries(normalizedHeadNames.map((head) => [head, '0'])));
            }}
            disabled={saving}
            style={{ marginBottom: 'var(--space-8)' }}
          >
            Ik heb alle maaikoppen gecontroleerd — alle messen aanwezig
          </button>
        )}

        {!normalizedHeadNames.length && (
          <div className="kleine-tekst" role="status">Het aantal maaikoppen is niet betrouwbaar beschikbaar. Sla deze controle over; er wordt geen head-ground-truth gemaakt.</div>
        )}

        <div className="maintenance-ground-truth-modal__heads-grid">
          {normalizedHeadNames.map((head) => (
            <label key={head} className="kleine-tekst" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span>
                <input
                  type="checkbox"
                  checked={checkedHeads[head] === true}
                  onChange={(event) => setCheckedHeads((prev) => ({ ...prev, [head]: event.target.checked }))}
                />{' '}{head} gecontroleerd
              </span>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="Niet van toepassing"
                className="input-bordered"
                value={missingByHead[head] ?? ''}
                onChange={(event) => {
                  const next = event.target.value;
                  setMissingByHead((prev) => ({ ...prev, [head]: next }));
                }}
              />
              <input type="number" min="0" step="1" placeholder="Vervangen (optioneel)" className="input-bordered" value={replacedByHead[head] ?? ''} onChange={(event) => setReplacedByHead((prev) => ({ ...prev, [head]: event.target.value }))} />
            </label>
          ))}
        </div>

        <label className="kleine-tekst" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 'var(--space-12)' }}>
          <span>Notitie (optioneel)</span>
          <textarea className="input-bordered" rows="3" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>

        <div className="maintenance-ground-truth-modal__footer">
          <button type="button" className="mwa-button secondary" onClick={onCancel} disabled={saving}>Annuleren</button>
          <button type="button" className="mwa-button secondary" onClick={onSkip} disabled={saving}>Overslaan</button>
          <button
            type="button"
            className="mwa-button"
            onClick={() => onSave({
              inspectionOutcome,
              missingBladeCount,
              damagedBladeCount,
              missingByHead,
              replacedByHead,
              confirmedHeadIds: normalizedHeadNames.filter((head) => checkedHeads[head] === true),
              notes,
            })}
            disabled={saving || !inspectionOutcome || !normalizedHeadNames.length}
          >
            Opslaan
          </button>
        </div>
      </div>
    </div>
  );
}
